import baseColors from 'styles/config/colors/baseColors';
import type { Config } from 'tailwindcss';

export const theme = {
  colors: {
    transparent: 'transparent',

    // Color ramp values
    base: {
      white: baseColors.white,
      black: baseColors.black,
      offWhite: baseColors.offWhite,
      grey: baseColors.newGrey,
      brand: baseColors.newBrand,
      red: baseColors.red,
      green: baseColors.green,
      yellow: baseColors.yellow,
      blue: baseColors.blue,
      purple: baseColors.purple,
    },

    // System colors
    'error-text-01': 'var(--tw-red-50)',
    'information-text-01': 'var(--tw-blue-50)',
    standard: 'var(--tw-purple-grey-50)',
    shadow: 'var(--tw-shadow)',

    // Text colors
    primary: 'var(--tw-grey-100)',
    'primary-inverse': 'var(--tw-white)',
    secondary: 'var(--tw-grey-70)',
    'secondary-inverse': 'var(--tw-grey-40)',
    'button-brand': 'var(--tw-grey-100)',
    helper: 'var(--tw-grey-50)',
    'helper-inverse': 'var(--tw-grey-70)',
    placeholder: 'var(--tw-grey-50)',
    'placeholder-inverse': 'var(--tw-grey-60)',
    brand: 'var(--tw-brand-60)',
    'brand-inverse': 'var(--tw-brand-50)',
    accent: 'var(--tw-brand-50)',
    disabled: 'var(--tw-grey-50)',
    'inverse-disabled': 'var(--tw-grey-70)',
    danger: 'var(--tw-red-50)',
    success: 'var(--tw-green-70)',
    warning: 'var(--tw-yellow-90)',
    info: 'var(--tw-blue-50)',

    // Background colors (semantic, theme-driven)
    background: 'var(--tw-background)',
    'background-page': 'var(--tw-background-page)',
    'background-primary': 'var(--tw-background-primary)',
    'background-primary-hover': 'var(--tw-background-primary-hover)',
    'background-primary-inverse': 'var(--tw-background-primary-inverse)',
    'background-primary-inverse-hover':
      'var(--tw-background-primary-inverse-hover)',
    'background-brand': 'var(--tw-background-brand)',
    'background-brand-inactive': 'var(--tw-background-brand-inactive)',
    'background-brand-inverse': 'var(--tw-background-brand-inverse)',
    'background-brand-hover': 'var(--tw-background-brand-hover)',
    'background-accent': 'var(--tw-background-accent)',
    'background-accent-inverse': 'var(--tw-background-accent-inverse)',
    'background-inverse': 'var(--tw-background-inverse)',
    'background-inverse-hover': 'var(--tw-background-inverse-hover)',
    'background-inverse-disabled': 'var(--tw-background-inverse-disabled)',
    'background-inverse-skeleton': 'var(--tw-background-inverse-skeleton)',
    'background-hover': 'var(--tw-background-hover)',
    'background-disabled-subtle': 'var(--tw-background-disabled-subtle)',
    'background-disabled-strong': 'var(--tw-background-disabled-strong)',
    'background-skeleton': 'var(--tw-background-skeleton)',
    'background-danger': 'var(--tw-background-danger)',
    'background-danger-hover': 'var(--tw-background-danger-hover)',
    'background-success': 'var(--tw-background-success)',
    'background-success-hover': 'var(--tw-background-success-hover)',
    'background-warning': 'var(--tw-background-warning)',
    'background-warning-hover': 'var(--tw-background-warning-hover)',
    'background-information': 'var(--tw-background-information)',
    'background-information-hover': 'var(--tw-background-information-hover)',
    'background-error': 'var(--tw-background-error)',
    'background-error-hover': 'var(--tw-background-error-hover)',
    'background-overlay': 'var(--tw-background-overlay)',

    // Border colors
    'border-primary': 'var(--tw-grey-100)',
    'border-brand': 'var(--tw-brand-50)',
    'border-brand-hover': 'var(--tw-grey-100)',
    'border-brand-active': 'var(--tw-grey-100)',
    'border-brand-inverse': 'var(--tw-brand-50)',
    'border-brand-inverse-hover': 'var(--tw-brand-40)',
    'border-brand-inverse-active': 'var(--tw-brand-50)',
    'border-subtle': 'var(--tw-border-subtle)',
    'border-subtle-inverse': 'var(--tw-border-subtle-inverse)',
    'border-table': 'var(--tw-border-table)',
    'border-strong': 'var(--tw-border-strong)',
    'border-strong-inverse': 'var(--tw-border-strong-inverse)',
    'border-underline': 'var(--tw-border-underline)',
    'border-underline-inverse': 'var(--tw-border-underline-inverse)',
    'border-focus': 'var(--tw-border-focus)',
    'border-inverse-focus': 'var(--tw-border-inverse-focus)',
    'border-disabled': 'var(--tw-border-disabled)',
    'border-danger': 'var(--tw-border-danger)',
    'border-success': 'var(--tw-border-success)',
    'border-warning': 'var(--tw-border-warning)',
    'border-info': 'var(--tw-border-info)',

    // Icon colors
    'icon-primary': 'var(--tw-icon-primary)',
    'icon-primary-hover': 'var(--tw-grey-20)',
    'icon-primary-brand': 'var(--tw-grey-100)',
    'icon-primary-inverse': 'var(--tw-white)',
    'icon-secondary': 'var(--tw-grey-70)',
    'icon-secondary-inverse': 'var(--tw-grey-40)',
    'icon-brand': 'var(--tw-brand-50)',
    'icon-brand-inverse': 'var(--tw-brand-50)',
    'icon-accent': 'var(--tw-purple-50)',
    'icon-button-brand': 'var(--tw-grey-100)',
    'icon-disabled': 'var(--tw-grey-50)',
    'icon-inverse-disabled': 'var(--tw-grey-70)',
    'icon-danger': 'var(--tw-red-50)',
    'icon-success': 'var(--tw-green-50)',
    'icon-warning': 'var(--tw-yellow-50)',
    'icon-info': 'var(--tw-blue-50)',

    // Generic surfaces
    'surface-brand-pill': 'var(--tw-surface-brand-pill)',

    // shadcn/ui semantic color keys (mapped via CSS variable aliases)
    shadcn: {
      background: 'var(--background)',
      foreground: 'var(--foreground)',
      card: {
        DEFAULT: 'var(--card)',
        foreground: 'var(--card-foreground)',
      },
      popover: {
        DEFAULT: 'var(--popover)',
        foreground: 'var(--popover-foreground)',
      },
      primary: {
        DEFAULT: 'var(--primary)',
        foreground: 'var(--primary-foreground)',
      },
      secondary: {
        DEFAULT: 'var(--secondary)',
        foreground: 'var(--secondary-foreground)',
      },
      muted: {
        DEFAULT: 'var(--muted)',
        foreground: 'var(--muted-foreground)',
      },
      accent: {
        DEFAULT: 'var(--accent)',
        foreground: 'var(--accent-foreground)',
      },
      destructive: {
        DEFAULT: 'var(--destructive)',
        foreground: 'var(--destructive-foreground)',
      },
      border: 'var(--border)',
      input: 'var(--input)',
      ring: 'var(--ring)',
    },
    sidebar: {
      DEFAULT: 'var(--sidebar-background)',
      foreground: 'var(--sidebar-foreground)',
      primary: {
        DEFAULT: 'var(--sidebar-primary)',
        foreground: 'var(--sidebar-primary-foreground)',
      },
      accent: {
        DEFAULT: 'var(--sidebar-accent)',
        foreground: 'var(--sidebar-accent-foreground)',
      },
      border: 'var(--sidebar-border)',
      ring: 'var(--sidebar-ring)',
    },
  },
  spacing: {
    '0': '0',
    xxs1: '1px',
    xxs2: '2px',
    xs1: '4px',
    xs2: '8px',
    xs3: '12px',
    sm1: '16px',
    sm2: '20px',
    sm3: '24px',
    sm4: '32px',
    md1: '40px',
    md2: '48px',
    md3: '64px',
    lg1: '80px',
    lg2: '120px',
    lg3: '160px',
    xl1: '200px',
    xl2: '240px',
    xl3: '260px',
    xxl1: '300px',
    xxl2: '400px',
    'icon-button': '10px',
  },
  borderRadius: {
    none: '0',
    xSmall: '4px',
    small: '8px',
    medium: '12px',
    large: '24px',
    xlarge: '32px',
    full: '9999px',
  },
  fontFamily: {
    heading: '"Proxima Nova", sans-serif',
    body: '"Proxima Nova", sans-serif',
    monospace: '"Roboto Mono", monospace',
  },
  fontSize: {
    'header-extra-large': '40px',
    'header-large': '24px',
    'header-medium': '18px',
    'header-regular': '16px',
    'header-small': '12px',
    'body-regular': '14px',
    'body-small': '12px',
    monospace: '12px',
  },
  fontWeight: {
    light: '200',
    normal: '300',
    regular: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },
  lineHeight: {
    exact: '1',
    tight: '1.2',
    normal: '1.5',
    loose: '2',
  },
  letterSpacing: {
    tight: '-0.8%',
    normal: '0',
  },
  screens: {
    xs: '500px',
    sm: '800px',
    md: '1200px',
    lg: '1400px',
  },
  boxShadow: {
    overlay: '0 1px 10px var(--tw-shadow)',
    none: '0 0 #000',
  },
  zIndex: {
    tooltip: '1500',
    toast: '1500',
    modal: '1400',
    dropdown: '1450',
    'popup-menu': '1300',
    overlay: '1300',
    'side-panel': '1200',
    'child-sidepanel': '1150',
    navbar: '1100',
    sticky: '900',
  },
  animation: {
    'fade-in': 'fade-in 0.3s ease-in-out',
    'fade-out': 'fade-out 0.3s ease-in-out',
    pulse: 'pulse 1.5s ease-in-out infinite',
    sweep: 'sweep 2s ease-in-out infinite',
    'collapsible-down': 'collapsible-down 0.15s ease-out',
    'collapsible-up': 'collapsible-up 0.15s ease-out',
    glow: 'glow 2s ease-in-out infinite',
  },
  keyframes: {
    'collapsible-down': {
      from: { height: '0' },
      to: { height: 'var(--radix-collapsible-content-height)' },
    },
    'collapsible-up': {
      from: { height: 'var(--radix-collapsible-content-height)' },
      to: { height: '0' },
    },
    'fade-in': {
      from: { opacity: '0' },
      to: { opacity: '1' },
    },
    'fade-out': {
      from: { opacity: '1' },
      to: { opacity: '0' },
    },
    pulse: {
      '0%': {
        transform: 'scale(1)',
      },
      '50%': {
        transform: 'scale(0.8)',
      },
      '100%': {
        transform: 'scale(1)',
      },
    },
    sweep: {
      '0%': {
        transform: 'translateX(-120%)',
      },
      '100%': {
        transform: 'translateX(450%)',
      },
    },
    glow: {
      '0%, 100%': {
        boxShadow: '0 0 8px rgba(99, 102, 241, 0.3)',
      },
      '50%': {
        boxShadow: '0 0 18px rgba(99, 102, 241, 0.5)',
      },
    },
    shimmer: {
      '0%': {
        backgroundPosition: '200% 0',
      },
      '100%': {
        backgroundPosition: '-200% 0',
      },
    },
  },
} satisfies Config['theme'];
